import { useContext, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MainTable } from "../../../../../components";
import { IMainTableColumn } from "../../../../../components/MainTable/MainTableInterfaces";
import { useDetailsColumns } from "./hooks/useDetailsColumns";
import { useStyles } from "./Details.styles";
import { HMICachedDataContext } from "../../../../../contexts/HMICachedDataContext/HMICachedDataContext";
import { FailureDetails } from "../FailureDetails/FailureDetails.view";
import moment from "moment-timezone";
import { getDowntimeDurationInHoursMinutesSeconds } from "utilities/utilities";
import { handleNameTranslation, translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable, FAILURE_TYPES_CODE_EXCEPTIONS } from "constants/constants";
import { HMIContext } from "contexts/HMIContext/HMIContext";
import { ShiftOptionsEnum } from "components/ShiftDropdown/ShiftDropdown.view";
import { IEventReport } from "./DetailsAPIInterfaces";

export const Details = () => {
    const { t, i18n } = useTranslation();
    const { store } = useContext(HMICachedDataContext);
    const tableColumns = useDetailsColumns() as unknown as IMainTableColumn[];
    const classes = useStyles();
    const userTimezone = moment.tz.guess();

    const today = useMemo(() => {
        const millTimezone = store.machineCenter?.production_line?.mill?.mill_data?.timezone;
        const now = millTimezone ? moment.utc().tz(millTimezone) : moment.utc().tz(userTimezone);
        return now.format("M/DD/YYYY");
    }, [store.machineCenter?.production_line?.mill?.mill_data?.timezone, userTimezone]);

    const { shift } = useContext(HMIContext);

    const lastTwoShiftsEventReports = useMemo(() => {
        const lastTwoShiftsEventReports =
            shift === ShiftOptionsEnum.current
                ? store.lastTwoShiftsEventReports?.currentShiftEventWithHours || []
                : store.lastTwoShiftsEventReports?.previousShiftEventWithHours || [];

        const millTimezone = store.machineCenter?.production_line?.mill?.mill_data?.timezone;

        const filteredEventReports: IEventReport[] = [];

        lastTwoShiftsEventReports
            .filter((eventReport) => {
                return eventReport.failure_type.code !== FAILURE_TYPES_CODE_EXCEPTIONS.BELOW_TRIGGER;
            })
            .forEach((eventReport) => {
                const pendingEditionDetails = store.editionsPending[eventReport.event_report_id];

                const failureType = pendingEditionDetails?.failureTypeDetails || eventReport.failure_type;
                const failureCode = pendingEditionDetails?.failureCodeDetails || eventReport.failure_code;
                const area = pendingEditionDetails?.areaDetails || eventReport.area;
                const functionalLocation =
                    pendingEditionDetails?.functionalLocationDetails || eventReport.functional_location;

                const defaultFailureTypeTranslatedName = translateDefaultEntity(
                    i18n.language,
                    failureType,
                    "name",
                    DefaultTranslationFieldsInTable.FAILURE_TYPE,
                );

                const defaultFailureCodeTranslatedName = translateDefaultEntity(
                    i18n.language,
                    failureCode,
                    "name",
                    DefaultTranslationFieldsInTable.FAILURE_CODE,
                );

                const mappedEventReport = {
                    event_report_id: eventReport.event_report_id,
                    start_date: eventReport.start_date,
                    end_date: eventReport.end_date,
                    duration: getDowntimeDurationInHoursMinutesSeconds(eventReport.validTime),
                    failure_type: defaultFailureTypeTranslatedName,
                    area:
                        area && failureType.is_location_specific
                            ? handleNameTranslation(i18n.language, area.translation.translation_names)
                            : "",
                    functional_location:
                        functionalLocation && failureType.is_location_specific
                            ? handleNameTranslation(i18n.language, functionalLocation.translation.translation_names)
                            : "",
                    failure_code: (failureCode && defaultFailureCodeTranslatedName) ?? "",
                    user_operator: eventReport.user_operator,
                    notes: eventReport.notes ? eventReport.notes : "",
                    hmiEdit: !!pendingEditionDetails,
                    fullEventReportObject: {
                        event_report_id: eventReport.event_report_id,
                        end_date: moment
                            .utc(eventReport.start_date)
                            .tz(millTimezone ?? userTimezone)
                            .format("MMM D, h:mm A"),
                        start_date: eventReport.end_date
                            ? moment
                                  .utc(eventReport.end_date)
                                  .tz(millTimezone ?? userTimezone)
                                  .format("MMM D, h:mm A")
                            : "",
                        duration: getDowntimeDurationInHoursMinutesSeconds(eventReport.validTime),
                        failure_type: failureType,
                        area: area && failureType.is_location_specific ? area : undefined,
                        functional_location:
                            functionalLocation && failureType.is_location_specific ? functionalLocation : "",
                        failure_code: failureCode ?? "",
                        user_operator: eventReport.user_operator,
                        notes: eventReport.notes ? eventReport.notes : "",
                        created_at: eventReport.created_at,
                        updated_at: eventReport.updated_at,
                    },
                };

                filteredEventReports.push(mappedEventReport);
            });

        return filteredEventReports.sort(
            (a, b) => new Date(b!.start_date).getTime() - new Date(a!.start_date).getTime(),
        );
    }, [
        shift,
        store.lastTwoShiftsEventReports?.currentShiftEventWithHours,
        store.lastTwoShiftsEventReports?.previousShiftEventWithHours,
        store.machineCenter?.production_line?.mill?.mill_data?.timezone,
        store.editionsPending,
        i18n.language,
        userTimezone,
    ]);

    return store.isEventReportEditing ? (
        <FailureDetails />
    ) : (
        <Box className={classes.detailsContainer}>
            <Typography variant="h1" className={classes.detailsTitle}>
                {`${t("HMI.details.title")} ${today}`}
            </Typography>
            {lastTwoShiftsEventReports && (
                <MainTable
                    columns={tableColumns}
                    rows={lastTwoShiftsEventReports}
                    paperCustomStyle={classes.paper}
                    customStyle={classes.tableContainer}
                    tableRowCustomStyle={classes.tableRow}
                    tableCellCustomStyle={classes.tableCell}
                    hmiShiftDetailsRowNoFailureCodeStyle
                />
            )}
        </Box>
    );
};
